import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Settings,
  Lock,
  Trash2,
  AlertTriangle,
  Loader2,
  Globe,
  Key,
  Webhook,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Send,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { BASE_URL } from "../lib/utils";

const SettingsPage = () => {
  const queryClient = useQueryClient();
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // API Keys State
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: "", expiresInDays: "" });
  const [createdApiKey, setCreatedApiKey] = useState(null);
  const [showCreatedKey, setShowCreatedKey] = useState(false);

  // Webhooks State
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ name: "", url: "", events: [] });

  // Fetch API Keys
  const { data: apiKeys = [], isLoading: loadingKeys } = useQuery({
    queryKey: ["apiKeys"],
    queryFn: () => api.get("/settings/api-keys").then((r) => r.data),
  });

  // Fetch Webhooks
  const { data: webhooks = [], isLoading: loadingWebhooks } = useQuery({
    queryKey: ["webhooks"],
    queryFn: () => api.get("/settings/webhooks").then((r) => r.data),
  });

  // Password mutation
  const passwordMutation = useMutation({
    mutationFn: (data) => api.put("/settings/password", data),
    onSuccess: () => {
      toast.success("Password aggiornata con successo!");
      setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Errore durante l'aggiornamento della password");
    },
  });

  // Clear data mutation
  const clearDataMutation = useMutation({
    mutationFn: () => api.delete("/settings/data"),
    onSuccess: () => {
      toast.success("Tutti i dati sono stati eliminati");
    },
    onError: () => {
      toast.error("Errore durante l'eliminazione dei dati");
    },
  });

  // API Key mutations
  const createApiKeyMutation = useMutation({
    mutationFn: (data) => api.post("/settings/api-keys", data),
    onSuccess: (response) => {
      setCreatedApiKey(response.data);
      setShowCreatedKey(true);
      queryClient.invalidateQueries(["apiKeys"]);
      setNewApiKey({ name: "", expiresInDays: "" });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Errore nella creazione della chiave API");
    },
  });

  const deleteApiKeyMutation = useMutation({
    mutationFn: (keyId) => api.delete(`/settings/api-keys/${keyId}`),
    onSuccess: () => {
      toast.success("Chiave API eliminata");
      queryClient.invalidateQueries(["apiKeys"]);
    },
    onError: () => {
      toast.error("Errore nell'eliminazione della chiave API");
    },
  });

  // Webhook mutations
  const createWebhookMutation = useMutation({
    mutationFn: (data) => api.post("/settings/webhooks", data),
    onSuccess: () => {
      toast.success("Webhook creato con successo");
      queryClient.invalidateQueries(["webhooks"]);
      setShowWebhookDialog(false);
      setNewWebhook({ name: "", url: "", events: [] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Errore nella creazione del webhook");
    },
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: (webhookId) => api.delete(`/settings/webhooks/${webhookId}`),
    onSuccess: () => {
      toast.success("Webhook eliminato");
      queryClient.invalidateQueries(["webhooks"]);
    },
    onError: () => {
      toast.error("Errore nell'eliminazione del webhook");
    },
  });

  const toggleWebhookMutation = useMutation({
    mutationFn: (webhookId) => api.put(`/settings/webhooks/${webhookId}/toggle`),
    onSuccess: () => {
      queryClient.invalidateQueries(["webhooks"]);
    },
  });

  const testWebhookMutation = useMutation({
    mutationFn: (webhookId) => api.post(`/settings/webhooks/${webhookId}/test`),
    onSuccess: (response) => {
      if (response.data.success) {
        toast.success("Test webhook inviato con successo!");
      } else {
        toast.error("Invio test webhook fallito");
      }
    },
    onError: () => {
      toast.error("Errore nell'invio del test webhook");
    },
  });

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    if (passwords.newPassword.length < 8) {
      toast.error("La nuova password deve essere di almeno 8 caratteri");
      return;
    }
    passwordMutation.mutate({
      currentPassword: passwords.currentPassword,
      newPassword: passwords.newPassword,
    });
  };

  const handleCreateApiKey = () => {
    if (!newApiKey.name.trim()) {
      toast.error("Inserisci un nome per la chiave API");
      return;
    }
    createApiKeyMutation.mutate({
      name: newApiKey.name,
      expiresInDays: newApiKey.expiresInDays ? parseInt(newApiKey.expiresInDays) : null,
    });
  };

  const handleCreateWebhook = () => {
    if (!newWebhook.name.trim() || !newWebhook.url.trim()) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (newWebhook.events.length === 0) {
      toast.error("Seleziona almeno un evento");
      return;
    }
    createWebhookMutation.mutate(newWebhook);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiato negli appunti");
  };

  const toggleWebhookEvent = (event) => {
    setNewWebhook((prev) => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter((e) => e !== event)
        : [...prev.events, event],
    }));
  };

  const eventLabels = {
    "email.opened": "Email Aperta",
    "link.clicked": "Link Cliccato",
    "campaign.milestone": "Milestone Campagna",
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-heading">Impostazioni</h1>
        <p className="text-muted-foreground">Gestisci le impostazioni del tuo account</p>
      </div>

      {/* Info Card */}
      <Card className="bg-white border border-slate-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Globe className="h-5 w-5 text-primary" />
            Informazioni Sistema
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Dominio di Tracking
              </Label>
              <p className="font-mono text-sm mt-1">{BASE_URL}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                URL Base Pixel
              </Label>
              <p className="font-mono text-sm mt-1">{BASE_URL}/pixel/[ID].gif</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys */}
      <Card className="bg-white border border-slate-100">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Chiavi API
            </CardTitle>
            <CardDescription>
              Gestisci le chiavi API per l'accesso programmatico
            </CardDescription>
          </div>
          <Dialog open={showApiKeyDialog} onOpenChange={setShowApiKeyDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-hover" data-testid="create-api-key-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuova Chiave
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea Nuova Chiave API</DialogTitle>
                <DialogDescription>
                  La chiave sarà visibile solo una volta. Salvala in un posto sicuro.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    placeholder="Es: Integrazione CRM"
                    value={newApiKey.name}
                    onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
                    data-testid="api-key-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza (opzionale)</Label>
                  <Select
                    value={newApiKey.expiresInDays}
                    onValueChange={(value) =>
                      setNewApiKey({ ...newApiKey, expiresInDays: value })
                    }
                  >
                    <SelectTrigger data-testid="api-key-expiry-select">
                      <SelectValue placeholder="Mai" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Mai</SelectItem>
                      <SelectItem value="30">30 giorni</SelectItem>
                      <SelectItem value="90">90 giorni</SelectItem>
                      <SelectItem value="365">1 anno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApiKeyDialog(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={createApiKeyMutation.isPending}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="confirm-create-api-key-btn"
                >
                  {createApiKeyMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Crea Chiave
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {/* Show created API key */}
          {createdApiKey && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Chiave API creata!</p>
                  <p className="text-sm text-green-700 mt-1">
                    Copia questa chiave ora. Non sarà più visibile.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white rounded border text-sm font-mono">
                      {showCreatedKey ? createdApiKey.apiKey : "•".repeat(40)}
                    </code>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowCreatedKey(!showCreatedKey)}
                    >
                      {showCreatedKey ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(createdApiKey.apiKey)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="link"
                    className="mt-2 p-0 h-auto text-green-700"
                    onClick={() => setCreatedApiKey(null)}
                  >
                    Ho copiato la chiave
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* API Keys list */}
          {loadingKeys ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nessuna chiave API creata</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  data-testid={`api-key-${key.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{key.name}</span>
                      {key.isActive ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Attiva
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          Scaduta
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="font-mono">{key.keyPrefix}</span>
                      <span>Creata: {new Date(key.createdAt).toLocaleDateString("it-IT")}</span>
                      {key.lastUsedAt && (
                        <span>
                          Ultimo uso: {new Date(key.lastUsedAt).toLocaleDateString("it-IT")}
                        </span>
                      )}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Elimina chiave API</AlertDialogTitle>
                        <AlertDialogDescription>
                          Sei sicuro di voler eliminare questa chiave API? Qualsiasi integrazione che
                          la utilizza smetterà di funzionare.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteApiKeyMutation.mutate(key.id)}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Elimina
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhooks */}
      <Card className="bg-white border border-slate-100">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Webhook className="h-5 w-5 text-primary" />
              Webhooks
            </CardTitle>
            <CardDescription>
              Ricevi notifiche in tempo reale per gli eventi di tracking
            </CardDescription>
          </div>
          <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-hover" data-testid="create-webhook-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Webhook
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crea Nuovo Webhook</DialogTitle>
                <DialogDescription>
                  Configura un endpoint per ricevere notifiche degli eventi
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    placeholder="Es: Notifiche Slack"
                    value={newWebhook.name}
                    onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                    data-testid="webhook-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL Endpoint</Label>
                  <Input
                    placeholder="https://..."
                    value={newWebhook.url}
                    onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                    data-testid="webhook-url-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eventi</Label>
                  <div className="space-y-2">
                    {Object.entries(eventLabels).map(([event, label]) => (
                      <div key={event} className="flex items-center space-x-2">
                        <Checkbox
                          id={event}
                          checked={newWebhook.events.includes(event)}
                          onCheckedChange={() => toggleWebhookEvent(event)}
                          data-testid={`webhook-event-${event}`}
                        />
                        <label htmlFor={event} className="text-sm cursor-pointer">
                          {label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowWebhookDialog(false)}>
                  Annulla
                </Button>
                <Button
                  onClick={handleCreateWebhook}
                  disabled={createWebhookMutation.isPending}
                  className="bg-primary hover:bg-primary-hover"
                  data-testid="confirm-create-webhook-btn"
                >
                  {createWebhookMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Crea Webhook
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loadingWebhooks ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Webhook className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nessun webhook configurato</p>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((webhook) => (
                <div
                  key={webhook.id}
                  className="p-4 bg-slate-50 rounded-lg"
                  data-testid={`webhook-${webhook.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{webhook.name}</span>
                        <Switch
                          checked={webhook.isActive}
                          onCheckedChange={() => toggleWebhookMutation.mutate(webhook.id)}
                          data-testid={`webhook-toggle-${webhook.id}`}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground font-mono mt-1 break-all">
                        {webhook.url}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {webhook.events.map((event) => (
                          <Badge key={event} variant="secondary" className="text-xs">
                            {eventLabels[event] || event}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => testWebhookMutation.mutate(webhook.id)}
                        disabled={testWebhookMutation.isPending}
                        title="Invia test"
                        data-testid={`webhook-test-${webhook.id}`}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Elimina webhook</AlertDialogTitle>
                            <AlertDialogDescription>
                              Sei sicuro di voler eliminare questo webhook? Non riceverai più
                              notifiche su questo endpoint.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteWebhookMutation.mutate(webhook.id)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className="bg-white border border-slate-100">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            Cambia Password
          </CardTitle>
          <CardDescription>
            Aggiorna la password del tuo account amministratore
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Password attuale</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwords.currentPassword}
                onChange={(e) =>
                  setPasswords({ ...passwords, currentPassword: e.target.value })
                }
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nuova password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwords.newPassword}
                onChange={(e) =>
                  setPasswords({ ...passwords, newPassword: e.target.value })
                }
                required
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma nuova password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwords.confirmPassword}
                onChange={(e) =>
                  setPasswords({ ...passwords, confirmPassword: e.target.value })
                }
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-hover"
              disabled={passwordMutation.isPending}
              data-testid="update-password-btn"
            >
              {passwordMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Aggiornamento...
                </>
              ) : (
                "Aggiorna Password"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-white border border-red-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Zona Pericolosa
          </CardTitle>
          <CardDescription>
            Azioni irreversibili. Procedi con cautela.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 border border-red-100 rounded-lg bg-red-50/50">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-medium text-red-900">Elimina tutti i dati</h4>
                <p className="text-sm text-red-700 mt-1">
                  Questa azione eliminerà tutte le campagne, destinatari, eventi e link.
                  L'account amministratore rimarrà intatto.
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="flex-shrink-0"
                    data-testid="clear-data-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Elimina Dati
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-destructive" />
                      Conferma eliminazione
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Sei sicuro di voler eliminare TUTTI i dati? Questa azione è irreversibile e
                      cancellerà:
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        <li>Tutte le campagne</li>
                        <li>Tutti i destinatari</li>
                        <li>Tutti gli eventi di apertura</li>
                        <li>Tutti i link tracciati</li>
                      </ul>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => clearDataMutation.mutate()}
                      className="bg-destructive hover:bg-destructive/90"
                      data-testid="confirm-clear-data-btn"
                    >
                      {clearDataMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Elimina tutto
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
